import { N8nModule } from '../../types';

export const module1: N8nModule = {
  id: 'zapier-intro',
  title: 'Introduction to Zapier',
  description: 'Get started with Zapier and learn the fundamentals of no-code automation.',
  content: `# Introduction to Zapier

## What is Zapier?

Zapier is a no-code automation platform that connects your favorite apps and services. With over 5,000+ app integrations, you can automate repetitive tasks without writing code.

## Key Concepts

### Zaps
A **Zap** is an automated workflow that connects your apps. Each Zap consists of:
- **Trigger**: The event that starts your workflow
- **Actions**: The tasks that are performed automatically

### Triggers
Events that start your Zap:
- New email received
- Form submission
- New row in spreadsheet
- Schedule (time-based)
- Webhook received

### Actions
Tasks performed when triggered:
- Send email
- Create record
- Update database
- Post to social media
- Send notification

## Getting Started

### Creating Your First Zap

1. **Choose a Trigger App**
   - Select the app that will start your automation
   - Example: Gmail, Google Forms, Slack

2. **Set Up the Trigger**
   - Define when the Zap should run
   - Test the trigger to ensure it works

3. **Add an Action**
   - Choose what happens when triggered
   - Configure the action settings

4. **Test Your Zap**
   - Run a test to verify everything works
   - Make adjustments as needed

5. **Turn On Your Zap**
   - Activate your automation
   - Monitor its performance

## Popular Use Cases

### Email Management
- Save Gmail attachments to Dropbox
- Create tasks from starred emails
- Forward important emails to Slack

### Lead Generation
- Add form responses to CRM
- Send welcome emails to new subscribers
- Notify sales team of new leads

### Social Media
- Post to multiple platforms simultaneously
- Save mentions to a spreadsheet
- Send alerts for brand mentions

### Project Management
- Create tasks from emails
- Update project status across tools
- Send daily summaries to team

## Best Practices

1. **Start Simple**: Begin with single-step Zaps before building complex workflows
2. **Test Thoroughly**: Always test your Zaps before activating them
3. **Use Filters**: Add conditions to control when actions run
4. **Monitor Performance**: Check your Zap history regularly
5. **Organize Your Zaps**: Use folders to keep workflows organized`,
  duration: '90 minutes',
  order: 1,
  objectives: [
    'Understand what Zapier is and how it works',
    'Learn the difference between triggers and actions',
    'Create your first simple Zap',
    'Test and activate automations',
    'Explore common use cases'
  ],
  keyConcepts: [
    'Triggers and Actions',
    'Zap creation process',
    'App integrations',
    'Testing workflows',
    'Automation best practices'
  ]
};
